/**
 * mapGeometry — Web Mercator pixel maths, extracted so it can be unit-tested.
 *
 * This lives in its own module for one reason: the accuracy-circle formula was
 * previously inlined in MapCanvas with the wrong exponent (`zoom + 8`), which
 * doubled metres-per-pixel and drew the circle at HALF its true size —
 * understating the fix's uncertainty at every zoom. An inline expression inside
 * an imperative DOM updater is reviewer-catchable at best; a pure exported
 * function is gate-catchable.
 *
 * The numbers mirror mapbox-gl 3.16 exactly (see
 * `node_modules/mapbox-gl/dist/mapbox-gl-dev.js`):
 *   - `Transform.worldSize = tileSize * scale` (:65777, :65947), with
 *     `tileSize = 512` and `scale = 2^zoom`, i.e. `worldSize = 2^(zoom + 9)` —
 *     NOT `2^(zoom + 8)`.
 *   - `mercatorZfromAltitude(1, lat) = 1 / circumferenceAtLatitude(lat)`, so
 *     `pixelsPerMeter = worldSize / circumferenceAtLatitude(lat)`.
 *   - `GeolocateControl._updateCircleRadius()` (:102852) sizes its circle as
 *     `Math.ceil(2 * accuracy * pixelsPerMeter)`.
 *
 * "Mirror mapbox exactly" is the governing constraint, and it outranks
 * geodetic correctness — see `EARTH_RADIUS_METERS` below.
 */

/**
 * The radius of mapbox-gl's sphere, in metres.
 *
 * mapbox-gl 3.16 (`node_modules/mapbox-gl/dist/mapbox-gl-dev.js`):
 *   :22853  `const earthRadius = 63710088e-1;`            // 6371008.8
 *   :22854  `const earthCircumference = 2 * Math.PI * earthRadius;`
 *   :23241  `function circumferenceAtLatitude(latitude) {`
 *   :23242  `  return earthCircumference * Math.cos(latitude * Math.PI / 180); }`
 *
 * DO NOT "correct" this to 6378137 / 40075016.686. That is WGS84's EQUATORIAL
 * figure and it is, in isolation, the geodetically more standard number — which
 * is exactly why someone will try. It is wrong *here*. 6371008.8 is the WGS84
 * mean radius, and it is the sphere mapbox itself renders on; the whole point of
 * this module is PIXEL PARITY with mapbox's own `GeolocateControl` circle, so it
 * must use mapbox's constant, not the more "correct" one. Using the equatorial
 * value inflates every distance by 0.1119%, which is invisible in the maths and
 * visible in the output: a 17.9 m fix at lat 0 / zoom 15 renders 15 px here and
 * 16 px in mapbox after the shared `Math.ceil`.
 *
 * `mapGeometry.test.ts` pins this against the INSTALLED mapbox-gl at runtime
 * (via `MercatorCoordinate`), so if the library ever changes its sphere, or this
 * line drifts from it, the suite fails rather than silently disagreeing.
 */
export const EARTH_RADIUS_METERS = 6371008.8;

/**
 * Circumference in metres — derived the way mapbox derives it, rather than
 * hardcoded as the product, so the two cannot fall out of step by a typo.
 */
export const EARTH_CIRCUMFERENCE_METERS = 2 * Math.PI * EARTH_RADIUS_METERS;

/** mapbox-gl's tile size; `worldSize = TILE_SIZE * 2^zoom` */
export const MAPBOX_TILE_SIZE = 512;

/** mapbox-gl's `circumferenceAtLatitude`: the parallel's length in metres. */
export const circumferenceAtLatitude = (lat: number): number =>
  EARTH_CIRCUMFERENCE_METERS * Math.cos((lat * Math.PI) / 180);

/**
 * Ground metres covered by one screen pixel at this latitude and zoom.
 *
 * `Math.abs` on the cosine only guards against a latitude past the poles being
 * passed in; between -90 and 90 the cosine is already non-negative.
 */
export const metersPerPixel = (lat: number, zoom: number): number =>
  Math.abs(circumferenceAtLatitude(lat)) /
  (MAPBOX_TILE_SIZE * Math.pow(2, zoom));

/**
 * Diameter, in CSS pixels, of a circle representing `accuracyMeters` of
 * uncertainty — i.e. a circle of RADIUS `accuracyMeters` on the ground.
 *
 * Deliberately identical to mapbox-gl's own `Math.ceil(2 * accuracy *
 * pixelsPerMeter)`, including the `ceil`, so our dot and mapbox's agree.
 *
 * Returns a non-finite value when the inputs make metres-per-pixel unusable
 * (exactly at a pole, or a NaN zoom); callers must guard with `Number.isFinite`.
 */
export const accuracyCircleDiameterPx = (
  accuracyMeters: number,
  lat: number,
  zoom: number
): number => Math.ceil((2 * accuracyMeters) / metersPerPixel(lat, zoom));

const mapGeometry = {
  circumferenceAtLatitude,
  metersPerPixel,
  accuracyCircleDiameterPx,
};

export default mapGeometry;
